/** API client for the Worker (same origin: Vite proxy in dev, Pages Function proxy in production). */
import { API_BASE } from '@/env';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...init });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) msg = b.error;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

export interface Me {
  id: string;
  name: string;
  avatarUrl: string | null;
  athleteId: number;
  stravaConnected: boolean;
}

export interface StravaUploadStatus {
  id: number;
  status: string;
  error: string | null;
  activity_id: number | null;
}

export interface RoomLobby {
  type: 'lobby';
  code: string;
  phase: 'lobby' | 'countdown' | 'riding' | 'finished';
  hostId: string | null;
  route: { hash: string; name: string; distanceM: number; loop: boolean } | null;
  riders: { id: string; name: string; color: string; ready: boolean; host: boolean; connected: boolean }[];
  startAt: number | null;
}

export const api = {
  loginUrl: () => `${API_BASE}/api/auth/strava/login`,
  me: () => call<Me>('/api/me'),
  logout: () => call<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  disconnect: () => call<{ ok: boolean }>('/api/auth/strava/disconnect', { method: 'POST' }),
  sync: <T>(body: unknown) =>
    call<T>('/api/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  putRoute: (id: string, json: string) =>
    call<{ ok: boolean; key: string }>(`/api/routes/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: json,
    }),
  getRoute: <T>(id: string) => call<T>(`/api/routes/${encodeURIComponent(id)}`),
  putRideFit: (id: string, fit: Uint8Array) =>
    call<{ ok: boolean; key: string }>(`/api/rides/${encodeURIComponent(id)}/fit`, {
      method: 'PUT',
      body: new Uint8Array(fit),
    }),
  getRideFit: async (id: string): Promise<Uint8Array> => {
    const res = await fetch(`${API_BASE}/api/rides/${encodeURIComponent(id)}/fit`, {
      credentials: 'include',
    });
    if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  },
  stravaUpload: (rideId: string, fit: Uint8Array, name: string, description: string) =>
    call<StravaUploadStatus>(
      `/api/strava/upload?rideId=${encodeURIComponent(rideId)}&name=${encodeURIComponent(name)}&description=${encodeURIComponent(description)}`,
      { method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: new Uint8Array(fit) },
    ),
  devLogin: (name: string) =>
    call<{ id: string; name: string }>('/api/auth/dev-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }),
  createRoom: (route: { hash: string; name: string; distanceM: number; loop: boolean }) =>
    call<{ code: string; lobby: RoomLobby }>('/api/rooms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ route }),
    }),
  roomInfo: (code: string) => call<RoomLobby>(`/api/rooms/${encodeURIComponent(code)}`),
  stravaUploadStatus: (uploadId: number, rideId: string) =>
    call<StravaUploadStatus>(`/api/strava/upload/${uploadId}?rideId=${encodeURIComponent(rideId)}`),
};
